import React, { FC } from 'react';
import { isWebView } from '@dxb-web-ui-framework/common/utils';
import { Box, Container, Grid, makeStyles, Paper } from '@dxb-web-ui-framework/design-system';
import { useScreenDetector } from '@dxb-web-ui-framework/common/hooks';
import { ScreenSplitLayoutProps, SpiltScreenTypes } from '../../types';
import Header from './Header';
import { TermsConditionSplitScreen } from './TermsConditionSplitScreen';
import { PublicSplitScreen } from './PublicSplitScreen';

const useStyles = makeStyles(({ breakpoints, spacing }) => ({
  root: {
    position: 'relative',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    [breakpoints.down('sm')]: {
      paddingTop: spacing(11),
      paddingBottom: spacing(2),
    },
  },
  page: {
    display: 'flex',
    [breakpoints.down('sm')]: {
      flexDirection: 'column',
      flexGrow: 1,
    },
  },
  pageContent: {
    width: '100%',
    maxWidth: '464px',
    height: 'fit-content',
    position: 'relative',
    [breakpoints.down('md')]: {
      maxWidth: 'unset',
    },
    [breakpoints.down('sm')]: {
      maxWidth: 'unset',
      height: '100%',
      padding: spacing(0, 3, 0, 3),
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      marginTop: spacing(2),
    },
  },
  leftContainer: {
    zIndex: 1,
    width: '60%',
    height: '100vh',
    minHeight: window.innerHeight,
    position: 'relative',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'white',
    padding: spacing(0, 0, 0, 10),
    flexDirection: 'row',
    [breakpoints.down('md')]: {
      flexDirection: 'column-reverse',
      textAlign: 'center',
      padding: spacing(0),
    },
    [breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  rightContainer: ({ shouldRemovePadding }: { shouldRemovePadding?: boolean }) => ({
    position: 'relative',
    width: '40%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    padding: spacing(0, 10, 0, 10),
    zIndex: 1,
    [breakpoints.down('sm')]: {
      width: '100%',
      display: 'flex',
      flexGrow: 1,
      minHeight: '100%',
      justifyContent: 'flex-start',
      padding: spacing(0, 0, shouldRemovePadding ? 0 : 6, 0),
    },
  }),
  title: {
    [breakpoints.down('md')]: {
      textAlign: 'center',
    },
  },
  pageTitle: {
    [breakpoints.down('sm')]: {
      display: 'none',
    },
  },
}));

const ScreenSplitLayout: FC<ScreenSplitLayoutProps> = ({
  moto,
  desktopBrandLogo,
  mobileBrandLogo,
  logoAlt,
  dataTestId,
  children,
  leftContainerType,
  shouldRemovePadding,
}) => {
  const classes = useStyles({ shouldRemovePadding });
  const { isExtraSmallScreen: isMobile } = useScreenDetector();

  return (
    <Container maxWidth={'xl'} disableGutters className={classes.root} data-testid={dataTestId}>
      <Header
        logo={isMobile ? mobileBrandLogo : desktopBrandLogo}
        logoAlt={logoAlt}
        isMobile={isMobile}
        isWebView={isWebView}
      />
      <Box component='main' className={classes.page}>
        <Grid container className={classes.leftContainer}>
          {leftContainerType === SpiltScreenTypes.TERMS_CONDITION && <TermsConditionSplitScreen />}
          {leftContainerType === SpiltScreenTypes.PUBLIC_APP && <PublicSplitScreen />}
        </Grid>

        <Paper elevation={isMobile ? 0 : 6} className={classes.rightContainer}>
          <Box className={classes.pageContent}>{children}</Box>
        </Paper>
      </Box>
    </Container>
  );
};

export { ScreenSplitLayout };
